"use client";

import { useEffect, useState, useRef, useCallback } from "react";

export interface UseDebounceOptions {
  delay?: number;
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
}

export interface UseDebounceReturn<T> {
  debouncedValue: T;
  isPending: boolean;
  cancel: () => void;
  flush: () => void;
}

/**
 * Хук для дебаунса значений с расширенными возможностями
 * @param value - значение для дебаунса
 * @param options - опции дебаунса
 * @returns объект с дебаунсированным значением и методами управления
 */
export function useDebounce<T>(
  value: T,
  options: UseDebounceOptions = {},
): UseDebounceReturn<T> {
  const { delay = 300, leading = false, trailing = true, maxWait } = options;

  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const [isPending, setIsPending] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const maxTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastCallTimeRef = useRef<number | undefined>(undefined);
  const lastInvokeTimeRef = useRef<number>(0);
  const leadingEdgeRef = useRef(false);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
    }
    lastCallTimeRef.current = undefined;
    lastInvokeTimeRef.current = 0;
    leadingEdgeRef.current = false;
    setIsPending(false);
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      setDebouncedValue(value);
      setIsPending(false);
      lastInvokeTimeRef.current = Date.now();
    }
  }, [value]);

  useEffect(() => {
    const now = Date.now();
    const isInvoking =
      leading &&
      leadingEdgeRef.current &&
      now - lastInvokeTimeRef.current >= delay;

    if (isInvoking) {
      setDebouncedValue(value);
      setIsPending(false);
      lastInvokeTimeRef.current = now;
      return;
    }

    cancel();

    if (leading && !leadingEdgeRef.current) {
      leadingEdgeRef.current = true;
    }

    lastCallTimeRef.current = now;
    setIsPending(true);

    const shouldInvoke = (now: number) => {
      const timeSinceLastCall = now - (lastCallTimeRef.current || 0);
      const timeSinceLastInvoke = now - lastInvokeTimeRef.current;

      return (
        lastCallTimeRef.current === undefined ||
        timeSinceLastCall >= delay ||
        timeSinceLastCall < 0 ||
        (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
      );
    };

    const timerExpired = () => {
      const now = Date.now();
      if (shouldInvoke(now)) {
        if (trailing) {
          setDebouncedValue(value);
          setIsPending(false);
          lastInvokeTimeRef.current = now;
        }
        return;
      }
      const timeSinceLastCall = now - (lastCallTimeRef.current || 0);
      const timeSinceLastInvoke = now - lastInvokeTimeRef.current;
      const timeWaiting = delay - timeSinceLastCall;
      const timeRemaining = maxWait
        ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
        : timeWaiting;

      timeoutRef.current = setTimeout(timerExpired, timeRemaining);
    };

    timeoutRef.current = setTimeout(timerExpired, delay);

    if (maxWait !== undefined) {
      maxTimeoutRef.current = setTimeout(() => {
        if (trailing) {
          setDebouncedValue(value);
          setIsPending(false);
          lastInvokeTimeRef.current = Date.now();
        }
      }, maxWait);
    }

    return () => {
      cancel();
    };
  }, [value, delay, leading, trailing, maxWait, cancel]);

  return {
    debouncedValue,
    isPending,
    cancel,
    flush,
  };
}

/**
 * Простой хук для дебаунса значений
 * @param value - значение для дебаунса
 * @param delay - задержка в миллисекундах
 * @returns дебаунсированное значение
 */
export function useDebounceValue<T>(value: T, delay: number = 300): T {
  const { debouncedValue } = useDebounce(value, { delay });
  return debouncedValue;
}

/**
 * Хук для дебаунса функций
 * @param callback - функция для дебаунса
 * @param delay - задержка в миллисекундах
 * @returns дебаунсированная функция
 */
export function useDebouncedCallback<T extends (...args: never[]) => unknown>(
  callback: T,
  delay: number = 300,
): T {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  ) as T;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}
